import {
    computed,
    defineComponent,
    onUnmounted,
    ref,
    watch,
} from "vue";

import Store from "../../store.js";
import {
    resolveMissingPermissions,
    type MissingPermissionsResult,
} from "../app.js";

type GuildChannel = {
    id          : string;
    name        : string;
    type        : number;
    position    : number;
    parentId    : string | null;
};

type PermissionAudit = Exclude<MissingPermissionsResult, null> & {
    checkedAt: number;
};

type ChannelGroup = {
    key         : string;
    category    : GuildChannel | null;
    channels    : GuildChannel[];
};

type ChannelViewMode = "issues" | "all";

function compareSnowflakes(a: string, b: string): number {
    try {
        return Number(BigInt(a) - BigInt(b));
    } catch {
        return a.localeCompare(b);
    }
}

function compareChannelPositions(a: GuildChannel, b: GuildChannel): number {
    return (a.position - b.position) || compareSnowflakes(a.id, b.id);
}

function organizeChannels(channels: GuildChannel[]): ChannelGroup[] {
    const categories = channels
        .filter((channel) => channel.type === 4)
        .sort(compareChannelPositions);
    const categoryIds = new Set(categories.map((category) => category.id));
    const channelsByCategory = new Map<string, GuildChannel[]>();
    const uncategorized: GuildChannel[] = [];

    for (const channel of channels) {
        if (channel.type === 4)
            continue;

        if (channel.parentId && categoryIds.has(channel.parentId)) {
            const siblings = channelsByCategory.get(channel.parentId) ?? [];
            siblings.push(channel);
            channelsByCategory.set(channel.parentId, siblings);
        } else {
            uncategorized.push(channel);
        }
    }

    const groups: ChannelGroup[] = [];

    if (uncategorized.length) {
        groups.push({
            key         : "uncategorized",
            category    : null,
            channels    : uncategorized.sort(compareChannelPositions),
        });
    }

    for (const category of categories) {
        groups.push({
            key         : category.id,
            category,
            channels    : (channelsByCategory.get(category.id) ?? []).sort(compareChannelPositions),
        });
    }

    return groups;
}

function getChannelIcon(channel: GuildChannel): string {
    switch (channel.type) {
        case 2:
            return "volume_up";
        case 4:
            return "folder";
        case 5:
            return "campaign";
        case 13:
            return "podcasts";
        case 15:
            return "forum";
        case 16:
            return "perm_media";
        default:
            return "tag";
    }
}

function getChannelTypeLabel(channel: GuildChannel): string {
    switch (channel.type) {
        case 2:
            return "Salon vocal";
        case 4:
            return "Catégorie";
        case 5:
            return "Salon d’annonces";
        case 13:
            return "Salon de conférence";
        case 15:
            return "Forum";
        case 16:
            return "Salon média";
        default:
            return "Salon textuel";
    }
}

function normalizeAudit(data: MissingPermissionsResult, checkedAt = 0): PermissionAudit {
    return {
        viewLogs           : data?.viewLogs ?? true,
        missingChannelIds  : data?.missingChannelIds ?? [],
        checkedAt,
    };
}

export default defineComponent({
    setup() {
        const channels        = ref<GuildChannel[]>([]);
        const audit           = ref<PermissionAudit | null>(null);
        const loading         = ref(false);
        const error           = ref<string | null>(null);
        const channelViewMode = ref<ChannelViewMode>("issues");
        const selectedChannelIds = ref<Set<string>>(new Set());
        const collapsedCategoryIds = ref<Set<string>>(new Set());
        const overrideFeedback = ref<string | null>(null);
        let auditController: AbortController | null = null;
        let overrideFeedbackTimer: number | null = null;

        const currentStatus = computed(() => (
            audit.value ?? normalizeAudit(resolveMissingPermissions(Store.selectedGuild))
        ));
        const missingChannelIds = computed(() => new Set(currentStatus.value.missingChannelIds));
        const channelGroups = computed(() => organizeChannels(channels.value));
        const visibleChannelGroups = computed(() => {
            if (channelViewMode.value === "all")
                return channelGroups.value;

            return channelGroups.value.flatMap((group) => {
                const categoryIsMissing = group.category
                    ? missingChannelIds.value.has(group.category.id)
                    : false;
                const inaccessibleChannels = group.channels
                    .filter((channel) => missingChannelIds.value.has(channel.id));

                if (!categoryIsMissing && inaccessibleChannels.length === 0)
                    return [];

                return [{
                    ...group,
                    channels: inaccessibleChannels,
                }];
            });
        });
        const issueCount = computed(() => (
            currentStatus.value.missingChannelIds.length
            + (currentStatus.value.viewLogs ? 0 : 1)
        ));
        const selectableMissingChannelIds = computed(() => {
            const availableIds = new Set(channels.value.map((channel) => channel.id));

            return currentStatus.value.missingChannelIds
                .filter((channelId) => availableIds.has(channelId));
        });
        const selectionPreset = computed<"all" | "missing" | null>(() => {
            const selectedIds = selectedChannelIds.value;

            if (channels.value.length > 0
                && channels.value.every((channel) => selectedIds.has(channel.id))
                && selectedIds.size === channels.value.length)
                return "all";

            if (selectableMissingChannelIds.value.length > 0
                && selectableMissingChannelIds.value.every((channelId) => selectedIds.has(channelId))
                && selectedIds.size === selectableMissingChannelIds.value.length)
                return "missing";

            return null;
        });

        watch(
            () => Store.selectedGuild?.id,
            () => {
                auditController?.abort();
                audit.value = null;
                channels.value = [];
                error.value = null;
                loading.value = false;
                channelViewMode.value = "issues";
                selectedChannelIds.value = new Set();
                collapsedCategoryIds.value = new Set();
                overrideFeedback.value = null;
            },
        );

        onUnmounted(() => {
            auditController?.abort();

            if (overrideFeedbackTimer !== null)
                window.clearTimeout(overrideFeedbackTimer);
        });

        function updateSelection(channelIds: Iterable<string>) {
            selectedChannelIds.value = new Set(channelIds);
            overrideFeedback.value = null;
        }

        function toggleChannelSelection(channelId: string) {
            const nextSelection = new Set(selectedChannelIds.value);

            nextSelection.has(channelId)
                ? nextSelection.delete(channelId)
                : nextSelection.add(channelId);

            updateSelection(nextSelection);
        }

        function toggleCategory(categoryId: string) {
            const nextCollapsedIds = new Set(collapsedCategoryIds.value);

            nextCollapsedIds.has(categoryId)
                ? nextCollapsedIds.delete(categoryId)
                : nextCollapsedIds.add(categoryId);

            collapsedCategoryIds.value = nextCollapsedIds;
        }

        function prepareOverride() {
            if (selectedChannelIds.value.size === 0)
                return;

            overrideFeedback.value = `Dérogation préparée localement pour ${selectedChannelIds.value.size} élément${selectedChannelIds.value.size > 1 ? "s" : ""}.`;

            if (overrideFeedbackTimer !== null)
                window.clearTimeout(overrideFeedbackTimer);

            overrideFeedbackTimer = window.setTimeout(() => {
                overrideFeedback.value = null;
                overrideFeedbackTimer = null;
            }, 4000);
        }

        async function verifyPermissions() {
            const guildId = Store.selectedGuild?.id;

            if (!guildId || loading.value)
                return;

            auditController?.abort();
            auditController = new AbortController();
            const controller = auditController;
            loading.value = true;
            error.value = null;

            try {
                const [ meResponse, channelsResponse ] = await Promise.all([
                    fetch("/api/@me", { signal: controller.signal }),
                    fetch("/api/channels", { signal: controller.signal }),
                ]);

                if (!meResponse.ok || !channelsResponse.ok)
                    throw new Error("permission-audit-failed");

                const [ me, nextChannels ] = await Promise.all([
                    meResponse.json(),
                    channelsResponse.json(),
                ]);

                if (controller.signal.aborted || Store.selectedGuild?.id !== guildId)
                    return;

                const refreshedGuild = Array.isArray(me?.guilds)
                    ? me.guilds.find((guild: { id: string }) => guild.id === guildId)
                    : null;

                if (!refreshedGuild || !Array.isArray(nextChannels))
                    throw new Error("permission-audit-invalid-response");

                Store.me = me;
                Store.selectedGuild = refreshedGuild;

                audit.value = normalizeAudit(
                    resolveMissingPermissions(refreshedGuild),
                    Date.now(),
                );
                channels.value = nextChannels;
                selectedChannelIds.value = new Set();
                collapsedCategoryIds.value = new Set();
                overrideFeedback.value = null;

            } catch (caughtError) {
                if (caughtError instanceof DOMException && caughtError.name === "AbortError")
                    return;

                error.value = "Impossible de vérifier les permissions pour le moment. Réessaie dans quelques instants.";

            } finally {
                if (auditController === controller) {
                    auditController = null;
                    loading.value = false;
                }
            }
        }

        function renderAccessWarning() {
            return (
                <span class="permission-audit-channel__warning" title="Le bot ne peut pas lire ce salon">
                    <i class="material-symbols-outlined" aria-hidden="true">warning</i>
                    <span>Accès manquant</span>
                </span>
            );
        }

        function renderChannel(channel: GuildChannel) {
            const inaccessible = missingChannelIds.value.has(channel.id);
            const selected = selectedChannelIds.value.has(channel.id);

            return (
                <div
                    key={channel.id}
                    class={{
                        "permission-audit-channel": true,
                        "permission-audit-channel--inaccessible": inaccessible,
                        "permission-audit-channel--selected": selected,
                    }}
                >
                    <i class="permission-audit-channel__icon material-symbols-outlined" aria-hidden="true">
                        {getChannelIcon(channel)}
                    </i>

                    <span class="permission-audit-channel__name">{channel.name}</span>
                    <span class="permission-audit-channel__type">{getChannelTypeLabel(channel)}</span>
                    {inaccessible && renderAccessWarning()}
                    <button
                        type="button"
                        class="permission-audit-selection-toggle"
                        aria-label={`${selected ? "Désélectionner" : "Sélectionner"} ${channel.name}`}
                        aria-pressed={selected}
                        onClick={() => toggleChannelSelection(channel.id)}
                    >
                        {selected && <i class="material-symbols-outlined" aria-hidden="true">check</i>}
                    </button>
                </div>
            );
        }

        function renderChannelGroup(group: ChannelGroup) {
            const categoryIsMissing = group.category
                ? missingChannelIds.value.has(group.category.id)
                : false;
            const categoryIsSelected = group.category
                ? selectedChannelIds.value.has(group.category.id)
                : false;
            const collapsed = group.category
                ? collapsedCategoryIds.value.has(group.category.id)
                : false;

            return (
                <section class={{
                    "permission-audit-channel-group": true,
                    "permission-audit-channel-group--collapsed": collapsed,
                }} key={group.key}>
                    <header class={{
                        "permission-audit-channel-group__header": true,
                        "permission-audit-channel-group__header--inaccessible": categoryIsMissing,
                        "permission-audit-channel-group__header--selected": categoryIsSelected,
                    }}>
                        {group.category
                            ? (
                                <button
                                    type="button"
                                    class="permission-audit-channel-group__collapse"
                                    aria-label={`${collapsed ? "Développer" : "Réduire"} la catégorie ${group.category.name}`}
                                    aria-expanded={!collapsed}
                                    onClick={() => toggleCategory(group.category!.id)}
                                >
                                    <i class="material-symbols-outlined" aria-hidden="true">keyboard_arrow_down</i>
                                </button>
                            )
                            : <span class="permission-audit-channel-group__collapse-placeholder" />}
                        <i class="permission-audit-channel-group__icon material-symbols-outlined" aria-hidden="true">
                            {group.category ? getChannelIcon(group.category) : "folder_off"}
                        </i>
                        <strong>{group.category?.name ?? "Sans catégorie"}</strong>
                        <span>{group.channels.length} salon{group.channels.length > 1 ? "s" : ""}</span>
                        {categoryIsMissing && renderAccessWarning()}
                        {group.category && (
                            <button
                                type="button"
                                class="permission-audit-selection-toggle"
                                aria-label={`${categoryIsSelected ? "Désélectionner" : "Sélectionner"} la catégorie ${group.category.name}`}
                                aria-pressed={categoryIsSelected}
                                onClick={() => toggleChannelSelection(group.category!.id)}
                            >
                                {categoryIsSelected && <i class="material-symbols-outlined" aria-hidden="true">check</i>}
                            </button>
                        )}
                    </header>

                    {!collapsed && group.channels.length > 0 && (
                        <div class="permission-audit-channel-group__channels">
                            {group.channels.map(renderChannel)}
                        </div>
                    )}
                </section>
            );
        }

        return () => (
            <section class="permission-audit-card" aria-labelledby="permission-audit-title" aria-busy={loading.value}>
                <header class="permission-audit-card__header">
                    <div class={{
                        "permission-audit-card__icon": true,
                        "permission-audit-card__icon--warning": issueCount.value > 0,
                    }}>
                        <i class="material-symbols-outlined" aria-hidden="true">
                            {issueCount.value > 0 ? "shield_locked" : "verified_user"}
                        </i>
                    </div>

                    <div class="permission-audit-card__copy">
                        <span>Permissions du bot</span>
                        <h2 id="permission-audit-title">Vérifier l’accès aux logs</h2>
                        <p>Contrôle l’accès au journal d’audit et aux salons uniquement lorsque tu en as besoin.</p>
                    </div>

                    <div class="permission-audit-card__actions">
                        <span class={{
                            "permission-audit-card__status": true,
                            "permission-audit-card__status--warning": issueCount.value > 0,
                        }}>
                            <i class="material-symbols-outlined" aria-hidden="true">
                                {issueCount.value > 0 ? "warning" : "check_circle"}
                            </i>
                            {issueCount.value > 0
                                ? `${issueCount.value} accès à vérifier`
                                : "Accès opérationnels"}
                        </span>

                        <button
                            type="button"
                            class="settings-button permission-audit-card__verify"
                            disabled={loading.value}
                            onClick={verifyPermissions}
                        >
                            <i class={{
                                "material-symbols-outlined": true,
                                "permission-audit-card__spinner": loading.value,
                            }} aria-hidden="true">
                                {loading.value ? "progress_activity" : "fact_check"}
                            </i>
                            {loading.value
                                ? "Vérification…"
                                : audit.value
                                    ? "Vérifier à nouveau"
                                    : "Vérifier les permissions"}
                        </button>
                    </div>
                </header>

                {error.value && (
                    <div class="permission-audit-error" role="alert">
                        <i class="material-symbols-outlined" aria-hidden="true">error</i>
                        {error.value}
                    </div>
                )}

                {audit.value && (
                    <div class="permission-audit-results">
                        <div class="permission-audit-summary">
                            <div class={{
                                "permission-audit-summary__item": true,
                                "permission-audit-summary__item--warning": !audit.value.viewLogs,
                            }}>
                                <i class="material-symbols-outlined" aria-hidden="true">
                                    {audit.value.viewLogs ? "check_circle" : "warning"}
                                </i>
                                <div>
                                    <strong>Journal d’audit</strong>
                                    <span>{audit.value.viewLogs ? "Accessible" : "Permission Voir le journal d’audit manquante"}</span>
                                </div>
                            </div>

                            <div class={{
                                "permission-audit-summary__item": true,
                                "permission-audit-summary__item--warning": audit.value.missingChannelIds.length > 0,
                            }}>
                                <i class="material-symbols-outlined" aria-hidden="true">
                                    {audit.value.missingChannelIds.length > 0 ? "warning" : "check_circle"}
                                </i>
                                <div>
                                    <strong>Salons du serveur</strong>
                                    <span>
                                        {audit.value.missingChannelIds.length > 0
                                            ? `${audit.value.missingChannelIds.length} accès manquant${audit.value.missingChannelIds.length > 1 ? "s" : ""}`
                                            : "Tous les salons sont accessibles"}
                                    </span>
                                </div>
                            </div>

                            <span class="permission-audit-summary__time">
                                Vérifié à {new Date(audit.value.checkedAt).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}
                            </span>
                        </div>

                        <div class="permission-audit-channels">
                            <div class="permission-audit-channels__toolbar">
                                <div>
                                    <strong>Accès aux salons</strong>
                                    <span>Ordre et catégories synchronisés avec le serveur.</span>
                                </div>

                                <div class="permission-audit-view-switch" role="group" aria-label="Affichage des salons">
                                    <button
                                        type="button"
                                        class={{ active: channelViewMode.value === "issues" }}
                                        aria-pressed={channelViewMode.value === "issues"}
                                        onClick={() => (channelViewMode.value = "issues")}
                                    >
                                        <i class="material-symbols-outlined" aria-hidden="true">warning</i>
                                        À corriger
                                        <span>{audit.value.missingChannelIds.length}</span>
                                    </button>
                                    <button
                                        type="button"
                                        class={{ active: channelViewMode.value === "all" }}
                                        aria-pressed={channelViewMode.value === "all"}
                                        onClick={() => (channelViewMode.value = "all")}
                                    >
                                        <i class="material-symbols-outlined" aria-hidden="true">format_list_bulleted</i>
                                        Liste des salons
                                        <span>{channels.value.length}</span>
                                    </button>
                                </div>
                            </div>

                            <div class="permission-audit-bulk-selection" role="group" aria-label="Sélection rapide">
                                <span>Sélection</span>
                                <button
                                    type="button"
                                    class={{ active: selectionPreset.value === "all" }}
                                    aria-pressed={selectionPreset.value === "all"}
                                    onClick={() => updateSelection(channels.value.map((channel) => channel.id))}
                                >
                                    <i class="material-symbols-outlined" aria-hidden="true">done_all</i>
                                    Tous
                                </button>
                                <button
                                    type="button"
                                    class={{ active: selectionPreset.value === "missing" }}
                                    aria-pressed={selectionPreset.value === "missing"}
                                    disabled={selectableMissingChannelIds.value.length === 0}
                                    onClick={() => updateSelection(selectableMissingChannelIds.value)}
                                >
                                    <i class="material-symbols-outlined" aria-hidden="true">warning</i>
                                    Manquants
                                </button>
                                {selectedChannelIds.value.size > 0 && (
                                    <button
                                        type="button"
                                        class="permission-audit-bulk-selection__clear"
                                        onClick={() => updateSelection([])}
                                    >
                                        Effacer
                                    </button>
                                )}
                            </div>

                            <div class="permission-audit-channel-list">
                                {visibleChannelGroups.value.length > 0
                                    ? visibleChannelGroups.value.map(renderChannelGroup)
                                    : (
                                        <div class="permission-audit-channel-list__empty">
                                            <i class="material-symbols-outlined" aria-hidden="true">verified</i>
                                            <strong>Aucun salon à corriger</strong>
                                            <span>Le bot peut lire l’historique de tous les salons du serveur.</span>
                                        </div>
                                    )}
                            </div>

                            <footer class="permission-audit-override-action">
                                <div class="permission-audit-override-action__icon">
                                    <i class="material-symbols-outlined" aria-hidden="true">rule_settings</i>
                                </div>
                                <div class="permission-audit-override-action__copy">
                                    <strong>
                                        {selectedChannelIds.value.size > 0
                                            ? `${selectedChannelIds.value.size} élément${selectedChannelIds.value.size > 1 ? "s" : ""} sélectionné${selectedChannelIds.value.size > 1 ? "s" : ""}`
                                            : "Sélectionne des salons"}
                                    </strong>
                                    <span>Attribue une dérogation LogsCord individuelle à la sélection.</span>
                                </div>
                                <span class="permission-audit-override-action__placeholder">Interface uniquement</span>
                                <button
                                    type="button"
                                    class="settings-button permission-audit-override-action__button"
                                    disabled={selectedChannelIds.value.size === 0}
                                    onClick={prepareOverride}
                                >
                                    <i class="material-symbols-outlined" aria-hidden="true">add_moderator</i>
                                    Attribuer une dérogation
                                </button>
                            </footer>

                            {overrideFeedback.value && (
                                <div class="permission-audit-override-feedback" role="status">
                                    <i class="material-symbols-outlined" aria-hidden="true">check_circle</i>
                                    {overrideFeedback.value} Aucun changement n’a été envoyé au serveur.
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </section>
        );
    },
});
